import React, { useEffect, useState, useCallback } from "react";

// All FE → BE communication goes through this
const API_BASE = window.APP_API_BASE || ''; // e.g. 'https://api.example.com'

/* ---------------- API HELPERS ---------------- */
async function apiLogin(id_token) {
  return fetch(`${API_BASE}/service_login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token })
  });
}

async function apiLogout() {
  return fetch(`${API_BASE}/service_logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
}

async function apiApps() {
  const res = await fetch(`${API_BASE}/service_apps`, {
    method: 'GET',
    credentials: 'include'
  });
  if (!res.ok) throw new Error(res.status);
  return res.json();
}

/* ---------------- HELPERS ---------------- */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    const padded = payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, '=');
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

/* ==============================
         MAIN COMPONENT
   ============================== */
export default function App() {
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ---------------- Load apps & session ---------------- */
  const fetchApps = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await apiApps();
      setApps(data.apps || []);
      if (data.user) setUser(data.user);
    } catch {
      // Not logged? Show sign-in.
      setUser(null);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  /* ---------------- Initialize Google Sign-In ---------------- */
  useEffect(() => {
    if (!window.google || user) return;

    const clientId = window.APP_GOOGLE_CLIENT_ID || '';
    if (!clientId) {
      console.warn("window.APP_GOOGLE_CLIENT_ID not set");
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response) => {
        if (!response?.credential) {
          setError("Google sign-in returned no credential.");
          return;
        }

        // Show decoded user instantly (optional)
        const payload = decodeJwtPayload(response.credential);
        if (payload) {
          setUser({ name: payload.name, email: payload.email, picture: payload.picture });
        }

        try {
          const r = await apiLogin(response.credential);
          if (r.ok) {
            await fetchApps(); // loads apps + backend user
          } else {
            const t = await r.text();
            setError(`Login failed: ${r.status} ${t}`);
            setUser(null);
          }
        } catch (err) {
          setError(String(err));
          setUser(null);
        }
      }
    });

    const btnContainer = document.getElementById("gsi-button");
    if (btnContainer && btnContainer.childElementCount === 0) {
      window.google.accounts.id.renderButton(btnContainer, { theme: "outline", size: "large" });
    }
  }, [user, fetchApps]);

  /* ---------------- Logout ---------------- */
  const handleLogout = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await apiLogout();
      if (res.ok) {
        setUser(null);
        setApps([]);
      } else {
        setError(`Logout failed (${res.status})`);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto", fontFamily: "system-ui, Arial" }}>
      <h1>Main Page (PoC v2)</h1>

      {loading && <div>Checking session…</div>}

      {/* Sign-in state */}
      {!user && !loading && (
        <div>
          <p>Sign in with Google to continue.</p>
          <div id="gsi-button"></div>
          <div style={{ marginTop: 12, color: "#666", fontSize: 13 }}>
            Tip: ensure <code>window.APP_GOOGLE_CLIENT_ID</code> is configured.
          </div>
        </div>
      )}

      {/* Logged state */}
      {user && (
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
          {user.picture && (
            <img
              src={user.picture}
              alt="avatar"
              style={{ width: 64, height: 64, borderRadius: 8 }}
            />
          )}
          <div>
            <div style={{ fontWeight: 700 }}>{user.name || user.email}</div>
            <div style={{ color: "#666" }}>{user.email}</div>
            <div style={{ marginTop: 8 }}>
              <button onClick={handleLogout} style={{ padding: "8px 12px", borderRadius: 6 }}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <hr />

      <h2>Your apps</h2>
      {!loading && apps.length === 0 && <div>No apps available.</div>}

      <ul>
        {apps.map((a, i) => (
          <li key={i} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  background: "#eee",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {a.icon ? (
                  <img src={a.icon} alt="" style={{ width: 32, height: 32 }} />
                ) : (
                  "▣"
                )}
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>{a.title}</div>
                <div style={{ fontSize: 13 }}>{a.description}</div>
                {a.url && (
                  <div style={{ marginTop: 6 }}>
                    <a href={a.url} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {error && <div style={{ marginTop: 12, color: "darkred" }}>Error: {error}</div>}
    </div>
  );
}